/* eslint-disable no-underscore-dangle */
import Hr from '@components/atoms/Hr';
import Section from '@components/atoms/Section';
import { Heading2, Paragraph } from '@components/atoms/Typography';
import BreadCrumbs from '@components/molecules/BreadCrumbs';
import PageContainer from '@components/molecules/PageContainer';
import RichTextSection from '@components/molecules/RichTextSection';
import { ButtonOnRichText } from '@components/molecules/SliceZone';
import DefaultHeader from '@components/organisms/headers/DefaultHeader';
import JoinSection from '@components/organisms/sections/JoinSection';
import FAQBlock from '@components/organisms/seo/FAQBlock';
import { CarrierId } from '@graphql/generated';
import COLORS from '@lib/constants/colors';
import ROUTES from '@lib/constants/routes';
import { aggregateRating } from '@lib/constants/seo';
import carriersContent from '@lib/data/contents/carriersContent';
import airtableBases from '@lib/helpers/airtableBases';
import { getInsurancePageInfo } from '@lib/helpers/texts';
import { GetStaticPaths, GetStaticProps } from 'next';
import { ProductJsonLd } from 'next-seo';
import Link from 'next/link';
import React from 'react';

interface Props {
  uid: CarrierId;
  compareCarriers: {
    slug: string;
    name: string | undefined;
  }[];
}

const CarrierPage = ({ uid, compareCarriers }: Props) => {
  const content = carriersContent[uid];

  const title = `${content.name}: Comprar e Renovar Seguro Online`;
  const description = `A ${content.name} é uma boa opção? Confira os detalhes de seguro da ${content.shortName}`;
  return (
    <PageContainer
      title={title}
      metaDescription={description}
      transparentNavbar
      transparentNavbarFontColor={COLORS.black}
      customAmpPage
    >
      <ProductJsonLd
        productName={title}
        description={description}
        aggregateRating={aggregateRating}
      />
      <DefaultHeader
        pastel
        title={content.name}
        buttonText="Ver Preços"
        subtitle={`Confira os produtos que a ${content.name} e como a Seguradora Trabalha para proteger seu veículo`}
      />
      <RichTextSection>
        <p>{content.about}</p>
        <h2>Produtos de seguro que a {content.name} trabalha.</h2>
        <p>
          A {content.name} trabalha com uma variedade de opções, confira alguns
          deles:
        </p>
        {content.products.map((p) => {
          const info = getInsurancePageInfo(p.pageId);
          return (
            <div key={p.pageId}>
              <h2>
                <Link href={ROUTES.insurancePage.carrier(p.pageId, content.id)}>
                  <a>
                    {content.shortName} {info?.productName}
                  </a>
                </Link>
              </h2>
              <p>
                A {content.shortName} oferece {p.plans.length} planos para o
                seguro de {info?.name}, aqui estão eles:
              </p>
              <ul>
                {p.plans.map((plan) => (
                  <li key={plan.name}>{plan.name}</li>
                ))}
              </ul>
              <p>
                Confira quanto custa o{' '}
                <Link href={ROUTES.insurancePage.home(p.pageId)}>
                  <a>seguro de {info?.name}</a>
                </Link>{' '}
                da {content.name} clicando no botão abaixo!
              </p>
              <ButtonOnRichText
                text={`Cotar Seguro ${content.shortName}`}
                href={ROUTES.startForm()}
              />
            </div>
          );
        })}
        <FAQBlock
          title={`Perguntas frequentes sobre a ${content.shortName}`}
          items={[
            {
              question: `A ${content.shortName} é confiável?`,
              answer: (
                <>
                  <Paragraph>
                    Sim, o seguro da {content.shortName} é confiável, ela é uma
                    seguradora fiscalizada pela SUSEP.
                  </Paragraph>
                </>
              ),
            },
            {
              question: `Como renovar o seguro da ${content.shortName}?`,
              answer: (
                <>
                  <Paragraph>
                    Você pode{' '}
                    <Link passHref href={ROUTES.startForm()}>
                      <a>renovar um seguro da {content.shortName}</a>
                    </Link>{' '}
                    em menos de 5 minutos com a Luna
                  </Paragraph>
                </>
              ),
            },
            {
              question: `Onde posso contratar o  seguro da ${content.shortName}?`,
              answer: (
                <>
                  <Paragraph>
                    Você pode visitar nosso site{' '}
                    <Link passHref href={ROUTES.root}>
                      <a>luna.ac</a>
                    </Link>
                    , comparar todas as opções de seguro da {content.shortName}.
                  </Paragraph>
                </>
              ),
            },
            {
              question: `Como posso contratar o seguro da ${content.shortName} online?`,
              answer: (
                <>
                  <Paragraph>
                    Você pode contratar um{' '}
                    <Link passHref href={ROUTES.startForm()}>
                      <a>seguro online da {content.shortName}</a>
                    </Link>{' '}
                    visitando o site da Luna. O nosso site compara os preços de
                    seguros das melhores seguradoras para você escolher a melhor
                    opção.
                  </Paragraph>
                </>
              ),
            },
            {
              question: `Eu posso renovar um seguro da ${content.shortName} depois de vencido?`,
              answer: (
                <>
                  <Paragraph>
                    Sim, se a apólice de seguro da {content.shortName} estiver
                    vencida, você ainda pode renová-la dentro do período de
                    carência de 90 dias a partir da data de vencimento.
                  </Paragraph>
                </>
              ),
            },
          ]}
        />
        <Hr />
        <Heading2>
          Compare a {content.shortName} com outras seguradoras:
        </Heading2>
        <ul>
          {compareCarriers
            .filter((c) => c.slug !== content.id)
            .map((c) => (
              <li key={c.slug}>
                <Link
                  passHref
                  href={ROUTES.carriers.compare([
                    { slug: content.id },
                    { slug: c.slug },
                  ])}
                >
                  <a>{`${content.shortName} vs ${c.name}`}</a>
                </Link>
              </li>
            ))}
        </ul>
      </RichTextSection>
      <Section style={{ paddingTop: 0 }}>
        <BreadCrumbs
          links={[
            {
              label: 'Luna',
              href: ROUTES.root,
            },
            {
              label: 'Seguradoras',
              href: ROUTES.carriers.home,
            },
            {
              label: content.shortName,
              href: ROUTES.carriers.carrier(content.id),
            },
          ]}
        />
      </Section>
      <JoinSection
        title={`Compare o seguro da ${content.shortName} com outras seguradoras`}
        description="A Luna vai encontrar o melhor seguro para você em menos de 1 minutos, é tudo online!"
        buttonText="Simular meu seguro"
      />
    </PageContainer>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = Object.values(carriersContent).map((content) =>
    ROUTES.carriers.carrier(content.id),
  );

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const { uid } = params as {
    uid: CarrierId;
  };

  const compareCarriers = await airtableBases.getCarriers();

  return {
    props: {
      uid,
      compareCarriers: compareCarriers.map((c) => ({
        slug: c.slug,
        name: c.nome,
      })),
    },
  };
};

export default CarrierPage;
